/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// DriveTelemetry Phase 1 — service layer. The ONE place that ties the native
// DriveTelemetryPlugin to the rating math and to RTDB.
//
// PRIVACY MODEL (mirrors ActivityContext — do not loosen without product
// sign-off):
//  - OFF by default. We only startMonitoring() + WRITE the rating when the user
//    has explicitly enabled the EXISTING /users/{uid}/activitySharingEnabled
//    opt-in. No new ungated sharing path is introduced.
//  - SafeCircle-only read: the rating doc lives at
//    /users/{uid}/liveState/driveRating, a sibling of liveState/activity, so it
//    inherits the SAME safeCircleReaders + sharingEnabled read rule. (That rule
//    is documented in docs/drive-telemetry-phase1-plan.md and must be deployed
//    separately — see the plan; this code does not deploy it.)
//
// TRIP HISTORY: the last 3 trip SCORES are kept device-locally (localStorage),
// not in RTDB — only the derived rating is shared. The displayed rating is a
// recency-weighted average of those trips so one noisy trip can't crater a good
// driver (see computeDisplayRating).
//
// PLATFORM: iOS only in Phase 1. On web/Android this service stays inert (the
// native plugin proxy would throw, so every native call is platform-guarded).

import { Capacitor } from "@capacitor/core";
import { ref, serverTimestamp, set } from "firebase/database";
import { auth, db } from "@/config/firebase";
import { DriveTelemetry } from "@/utils/driveTelemetry";
import {
  type DriveRating,
  type TripScore,
  type TripSummary,
  computeDisplayRating,
  retainTrips,
  scoreTrip,
} from "@/utils/driveTelemetry/driveRating";

function isNativeIos(): boolean {
  return Capacitor.getPlatform() === "ios";
}

function historyKey(uid: string): string {
  return `driveTelemetry.tripScores.${uid}`;
}

/** Load the retained trip scores for a user (device-local). Tolerant of
 *  missing/corrupt storage — returns []. */
export function loadTripHistory(uid: string): TripScore[] {
  try {
    const raw = localStorage.getItem(historyKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TripScore[]) : [];
  } catch {
    return [];
  }
}

function saveTripHistory(uid: string, scores: TripScore[]): void {
  try {
    localStorage.setItem(historyKey(uid), JSON.stringify(scores));
  } catch {
    /* storage full / unavailable — non-fatal, rating just won't persist */
  }
}

function ratingDocRef(uid: string) {
  return ref(db, `users/${uid}/liveState/driveRating`);
}

/**
 * Process one trip summary: score it, fold into the device-local last-3
 * history, recompute the displayed rating, and (only when sharing is on) write
 * the rating to RTDB. Pure-ish: the scoring/averaging is delegated to the pure
 * core; this function owns the persistence + the sharing gate.
 *
 * Returns the new displayed rating (or null if there were somehow no trips).
 * Exposed (not just internal) so it can be unit-tested with a fake summary.
 */
export async function ingestTripSummary(
  uid: string,
  summary: TripSummary,
  sharingEnabled: boolean,
): Promise<DriveRating | null> {
  const existing = loadTripHistory(uid);
  const tripScore = scoreTrip(summary);
  const retained = retainTrips(existing, tripScore);
  saveTripHistory(uid, retained);

  const rating = computeDisplayRating(retained);
  if (!rating) return null;
  console.log(
    `[driveTelemetry] ingestTripSummary — scored trip, new rating tier=${rating.tier} score=${rating.score} trips=${rating.tripCount} lastTripEndTs=${rating.lastTripEndTs} (sharingEnabled=${sharingEnabled})`,
  );

  // Only WRITE when the user has the existing activity-sharing opt-in on. No
  // new ungated path. If sharing is off, we still keep local history (so a
  // later opt-in shows an accurate rating immediately) but never write.
  if (sharingEnabled) {
    try {
      await set(ratingDocRef(uid), {
        tier: rating.tier,
        score: rating.score,
        tripCount: rating.tripCount,
        lastTripEndTs: rating.lastTripEndTs,
        updatedAt: serverTimestamp(),
        // The read rule gates on data.child('sharingEnabled') === true (same as
        // liveState/activity), so the flag MUST live in the doc itself or the
        // SafeCircle read always denies. Mirrors ActivityContext's write.
        sharingEnabled: true,
      });
      console.log(
        `[driveTelemetry] ingestTripSummary — RTDB write OK → users/${uid}/liveState/driveRating`,
      );
    } catch (err) {
      // Rule denial (e.g. sharing flipped off mid-write) is non-fatal — the
      // local history is the source of truth and will re-sync on next trip.
      console.warn(
        "[driveTelemetry] ingestTripSummary — RTDB write DENIED/failed:",
        err,
      );
    }
  } else {
    console.log(
      "[driveTelemetry] ingestTripSummary — sharing OFF, kept local history but did NOT write RTDB",
    );
  }

  return rating;
}

/**
 * Singleton controller for the native drive-telemetry queue. ActivityContext
 * calls start() when the user's activity-sharing opt-in is ON (and on every
 * foreground via the same gate) and stop() when it flips OFF — reusing the SAME
 * opt-in as live activity. iOS-only; no-op elsewhere.
 *
 * Delivery model: trips almost always END in the background (WebView suspended),
 * so native PERSISTS each completed trip summary to a queue. start() drains that
 * queue via flushPending() — which returns the summaries and clears the native
 * queue atomically across the bridge resolve, so nothing is lost. There is NO
 * live event listener (a background notify would be dropped).
 */
class DriveTelemetryController {
  private uid: string | null = null;
  private sharingEnabled = false;

  /** Enable native monitoring and drain any queued trip summaries. Idempotent;
   *  safe to call on every app foreground. */
  async start(uid: string): Promise<void> {
    if (!isNativeIos()) return;
    this.uid = uid;
    this.sharingEnabled = true;

    try {
      await DriveTelemetry.startMonitoring();
      console.log(`[driveTelemetry] controller.start() — native startMonitoring OK (uid=${uid})`);
      await this.flush();
    } catch (err) {
      // Native unavailable (older OS / no motion coprocessor) — stay inert.
      console.warn("[driveTelemetry] controller.start() — startMonitoring threw, going inert:", err);
      await this.stop();
    }
  }

  /**
   * Drain the native queue of completed trip summaries through the rating
   * pipeline. Safe to call any time; no-op if nothing is queued or no uid yet.
   * Called from start() AND from an app-foreground listener — because a trip
   * usually ends backgrounded while JS stays alive, so the gate-driven start()
   * effect (deps [active,uid]) does NOT re-fire on a warm foreground; the queued
   * summary would otherwise sit un-rated until the next cold start.
   */
  async flush(): Promise<void> {
    if (!isNativeIos()) return;
    // Prefer the uid start() set; fall back to the authed user. The foreground
    // flush listener (ActivityContext) can fire before/without start() having
    // set this.uid — the gate effect [active,uid] does NOT re-fire on a warm
    // foreground, so on many app-reopens this.uid is still null even though a
    // trip has queued. Silently returning there dropped the drain (queue kept
    // growing, rating froze) — the primary freeze cause. Recover the uid from
    // auth.currentUser and, so subsequent ingests carry it, latch it onto the
    // controller. sharingEnabled defaults false until start() runs; that only
    // gates the RTDB WRITE, so the local history (what the mock card reads)
    // still advances — and the next start() re-writes RTDB from that history.
    const uid = this.uid ?? auth.currentUser?.uid ?? null;
    if (!uid) {
      // Genuinely no authed user (signed out) → nothing to drain to. Leave the
      // native queue intact for a later flush once auth resolves.
      console.warn("[driveTelemetry] flush() SKIPPED — no uid (signed out?)");
      return;
    }
    if (!this.uid) {
      this.uid = uid;
      console.log(`[driveTelemetry] flush() — recovered uid from auth (start() had not set it) uid=${uid}`);
    }
    try {
      const { summaries } = await DriveTelemetry.flushPending();
      console.log(
        `[driveTelemetry] flush() drained ${summaries.length} queued trip summary(ies) (sharingEnabled=${this.sharingEnabled})`,
      );
      for (const summary of summaries) {
        await ingestTripSummary(uid, summary, this.sharingEnabled);
      }
    } catch (err) {
      // Native unavailable — leave the queue for the next attempt.
      console.warn("[driveTelemetry] flush() failed (native throw):", err);
    }
  }

  /** Stop native monitoring; keeps device-local history intact. Idempotent. */
  async stop(): Promise<void> {
    this.sharingEnabled = false;
    if (!isNativeIos()) return;
    try {
      await DriveTelemetry.stopMonitoring();
    } catch {
      /* already stopped / unavailable */
    }
  }
}

export const driveTelemetryController = new DriveTelemetryController();
